import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';

import { useApiContext } from 'context/ApiContext';
import { KEYS } from 'helpers/react-query.config';
import { useGlobalContext } from 'context/GlobalContext';
import { ParamsSurvey } from 'api/insight';
import { AxiosServiceError } from 'interfaces/general';

function SurveySearchService({
  isSearch = false,
  params,
  idTemplate,
  IdTicket
}: {
  params?: ParamsSurvey;
  idTemplate?: number;
  IdTicket?: number;
  isSearch?: boolean;
}) {
  const { showApiError } = useGlobalContext();
  const { insight: insightApi } = useApiContext();
  const { t } = useTranslation('global');

  const {
    data: surveys,
    isLoading: isSurveysLoading,
    isRefetching: isRefetchingSurvey
  } = useQuery(
    [KEYS.insight.survey.getAllTemplate, params],
    () => insightApi.survey.getAllTemplates({ ...params }),
    {
      enabled: isSearch,
      refetchOnWindowFocus: false,
      select: ({ data }) => data?.data,
      onError: (error) =>
        showApiError(error, t('survey.messages.errorGetAllTemplates'))
    }
  );

  const { data: surveyDetail, isLoading: isSurveyLoading } = useQuery(
    [KEYS.insight.survey.getTemplateById(idTemplate)],
    () => insightApi.survey.getTemplateById(idTemplate),
    {
      cacheTime: 0,
      staleTime: 0,
      enabled: !!idTemplate,
      refetchOnWindowFocus: false,
      select: ({ data }) => data?.data || [],
      onError: (error: AxiosServiceError) => {
        showApiError(error, t('common.commonMessages.genericRequestError'));
      }
    }
  );

  const { data: surveyDetailNoti, isLoading: isSurveyNotiLoading } = useQuery(
    [KEYS.insight.survey.getTemplateByIdTicket(IdTicket)],
    () => insightApi.survey.getTemplateByIdTicket(IdTicket),
    {
      cacheTime: 0,
      staleTime: 0,
      enabled: !!IdTicket,
      refetchOnWindowFocus: false,
      select: ({ data }) => data?.data || [],
      onError: (error: AxiosServiceError) => {
        showApiError(error, t('common.commonMessages.genericRequestError'));
      }
    }
  );

  const {
    data: surveyToExport,
    refetch: refetchExport,
    isLoading: isLoadingExport
  } = useQuery(
    ['export-templates-survey'],
    () => insightApi.survey.getAllTemplates({ ...params, IsExport: true }),
    {
      select: ({ data: { data } }) =>
        data?.items?.map((item) => ({
          ...item,
          enabled: item?.enabled
            ? t('common.commonProperties.active')
            : t('common.commonProperties.inactive')
        })) || [],
      enabled: false,
      onError: (error) => {
        showApiError(error, t('common.commonMessages.genericRequestError'));
      },
      refetchOnWindowFocus: false
    }
  );

  const handleExport = () => refetchExport();

  return {
    surveys: {
      data: surveys,
      isLoading: isSurveysLoading,
      isRefetching: isRefetchingSurvey
    },
    surveyTemplateById: {
      data: surveyDetail,
      isLoading: isSurveyLoading
    },
    surveyTemplateByIdTicket: {
      data: surveyDetailNoti,
      isLoading: isSurveyNotiLoading
    },
    surveyToExport: {
      data: surveyToExport,
      isLoading: isLoadingExport
    },
    handleExport
  };
}

export default SurveySearchService;
